import React, { useContext } from "react";
import { Navigate } from "react-router-dom";

import { useGetCurrentUser } from "../store";

import { UserType } from "../types";
import { userHasPermission } from "../types-helpers";

import { AuthContext } from "../context";
import ClearCallLoadingLogo from "../components/clear-call-loading-logo";

/**
 * Props for the ProtectedRoute component
 */
export type ProtectedRouteProps = {
  /** Path to redirect to when authentication is required */
  authenticationPath: string;
  /** Minimum user level required to access this route */
  requiredUserLevel: UserType;
  /** The component/content to render when access is granted */
  outlet: JSX.Element;
};

/**
 * ProtectedRoute component that handles authentication and authorization
 * 
 * This component serves as a wrapper for routes that require user authentication
 * and specific permission levels. It shows a loading spinner while checking
 * user status, then either renders the protected content or redirects to login.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  authenticationPath,
  requiredUserLevel,
  outlet,
}: ProtectedRouteProps) => {

  const { userId } = useContext(AuthContext)
  const isAuthenticated = userId.length > 0

  // Fetch current user data and loading state
  const { data: currentUser, isLoading } = useGetCurrentUser();

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (<ClearCallLoadingLogo />)
  }
  // Check if user is authenticated and has required permissions
  else if (isAuthenticated && userHasPermission(requiredUserLevel, currentUser)) {
    // User has access - render the protected content
    return outlet;
  } else {
    // User is not authenticated or lacks permissions - redirect to login
    return <Navigate to={{ pathname: authenticationPath }} />;
  }
};

export default ProtectedRoute;
